import React, { CSSProperties, Suspense } from 'react'
import { filter } from 'lodash'
import { isBrowser, isTablet } from 'react-device-detect'
import { colors } from '@lmc/common/constants'
import { getNominative } from '../../common/utils'
import { gaEvent } from '../../common/services/analytics'
import CarePlanPdfExport from '../pdf/CarePlanPdfExport'
import LastUpdateBox from './LastUpdateBox'
import ResidentQuoteBox from './ResidentQuoteBox'
import { ErrorBox } from '../generic'
import { GlyphButton } from '../../LEGACY/elemental'
import { css, StyleSheet } from 'aphrodite/no-important'
import Field from '../fields'
import { ButtonOLD, Text } from '@lmc/ui-kit'
import Glyph from '../../LEGACY/Glyph'
import { CarePlan, Carer } from '@lmc/ts-common'

interface CarePlanSummaryHeaderProps {
  carePlan: CarePlan
  carer?: Carer | null
  errors: any[] // Update the type to match your error shape
  fields: any // Update the type to match your fields shape
  formId: string | null
  setFormField: (params: { key: string; value: any }) => void
  onClickBack: () => void
  onClickHistory: () => void
  rightSection?: () => React.ReactNode
  resident: {
    name: {
      first: string
      last: string
    }
  } | null
  isInitialAssessment: boolean
  logs?: any
  carePlanNominative?: any
}

class CarePlanSummaryHeader extends React.Component<
  any,
  { editTitle: boolean }
> {
  state = {
    editTitle: false,
  }

  onClickHistory = () => {
    const { carePlan, onClickHistory } = this.props

    onClickHistory()
    gaEvent({
      category: 'CarePlans',
      action: `Open History`,
      label: carePlan.carePlanAdl.name,
    })
  }

  renderInnerTitle = () => {
    const {
      carePlan,
      carer,
      // @ts-ignore
      logs,
      formId,
      resident,
      // @ts-ignore
      carePlanNominative,
      rightSection,
    } = this.props

    if (!resident) {
      return typeof rightSection === 'function' ? rightSection() : null
    }

    const pdfTitle = `${resident.name.first} ${
      resident.name.last
    } - ${getNominative(carePlanNominative, 'Care Plan')} - ${
      carePlan.carePlanAdl.name
    }`
    return (
      <div style={styles.buttonRow}>
        {!carePlan.isRevision && (
          <ButtonOLD
            disabled={!!formId}
            // @ts-ignore
            id="care-plan-history-button"
            dataTestId="care-plan-history"
            onClick={() => this.onClickHistory()}
            style={{ marginRight: 10 }}
            type="secondary"
            // @ts-ignore
            icon={<Glyph name="book" />}
          >
            History
          </ButtonOLD>
        )}
        {isDesktop && (
          <CarePlanPdfExport
            carePlan={carePlan}
            carePlanNominative={carePlanNominative}
            carer={carer}
            logs={filter(logs, { deleted: false })}
            disabled={!!formId}
            // @ts-ignore
            id="care-plan-pdf-download-button"
            resident={resident}
            title={pdfTitle}
            reviewComment={carePlan.reviewComment}
          />
        )}
      </div>
    )
  }

  renderHeader = () => {
    const { carePlan, onClickBack, resident } = this.props

    const backText = carePlan.isRevision ? 'History' : 'Dashboard'

    if (!resident) {
      return (
        <div style={styles.headerContainer}>
          <a onClick={carePlan.isRevision ? this.onClickHistory : onClickBack}>
            <span className="octicon octicon-chevron-left" /> {backText}
          </a>
        </div>
      )
    }

    return (
      <div style={styles.headerContainer}>
        <a onClick={carePlan.isRevision ? this.onClickHistory : onClickBack}>
          <span className="octicon octicon-chevron-left" /> {backText}
        </a>
      </div>
    )
  }

  onEditTitle = () => {
    const { carePlan, setFormField } = this.props

    this.setState({ editTitle: true })
    setFormField({
      key: 'customName',
      value: carePlan.customName || carePlan.carePlanAdl.name,
    })
  }

  renderTitleField = () => {
    const { fields, carePlan, formId, isInitialAssessment } = this.props
    return this.state.editTitle ? (
      <Suspense fallback={<div>Loading...</div>}>
      <Field
        className="lmc-sub-card-edit"
        keys="customName"
        layout="no-label"
        formId={formId}
        style={styles.titleInput}
        type="text"
        values={fields}
      />
      </Suspense>
    ) : (
      <h2 id="adl-name" style={styles.adlName}>
        {carePlan.customName || carePlan.carePlanAdl?.name}
        {!isInitialAssessment && formId && (
          // @ts-ignore
          <GlyphButton
            className={css(classes.titleEditBtn)}
            size="small"
            glyph="pencil"
            onClick={this.onEditTitle}
          />
        )}
      </h2>
    )
  }

  renderTitle = () => {
    const { carePlan } = this.props
    const adl = carePlan.carePlanAdl

    return (
      <div style={styles.titleContainer}>
        <div
          style={
            {
              ...styles.iconContainer,
              background: adl?.color || colors.darkgrey,
            } as CSSProperties
          }
        >
          <img src={adl?.icon} style={styles.icon} alt="" />
        </div>
        <div
          style={{
            ...styles.adlNameContainer,
          }}
        >
          {this.renderTitleField()}
        </div>
        {this.renderInnerTitle()}
      </div>
    )
  }

  renderLastReview = (carePlan: any, carer: Carer | null) => {
    const { isInitialAssessment } = this.props
    if (carePlan.editedOn && carePlan.editedBy && !carer) return null

    const carePlanTitle = carePlan.isRevision ? 'Reviewed on' : 'Last review'
    const title = isInitialAssessment ? 'Last update' : carePlanTitle
    return (
      <LastUpdateBox
        carer={carer}
        date={carePlan.editedOn}
        noDateText="No reviews yet"
        title={title}
        comment={carePlan.reviewComment}
      />
    )
  }

  renderOutcomes = () => {
    const { carePlan, fields, formId, resident } = this.props

    if (!resident) return null

    const fieldProps = formId && {
      className: 'lmc-sub-card-edit',
      formId,
      keys: 'outcomes',
      label: 'Outcomes',
      layout: 'no-label',
      type: 'textarea',
      values: fields,
    }
    return (
      <div style={{ paddingTop: 48 }}>
        <Text as="h4" style={styles.outComesTitle}>
          Desired outcomes
        </Text>
        <ResidentQuoteBox
          fieldProps={fieldProps}
          resident={resident}
          style={styles.quoteBox}
          text={carePlan.outcomes || carePlan.carePlanAdl.outcomes}
        />
      </div>
    )
  }

  render() {
    const { carePlan, carer, errors, resident, formId } = this.props
    if (!carePlan) return null
    return (
      <div>
        {this.renderHeader()}
        {this.renderTitle()}
        <ErrorBox errors={errors} style={styles.errors} />
        {this.renderLastReview(carePlan, carer as Carer)}
        {this.renderOutcomes()}
      </div>
    )
  }
}

const isDesktop = isBrowser || isTablet

const classes = StyleSheet.create({
  titleEditBtn: {
    background: 'transparent !important',
    position: 'relative',
    top: -1,
    height: 30,
    color: `${colors.black} !important`,
    marginLeft: 6,
    transition: 'all .2s ease',
    ':hover': {
      background: `${colors.orange} !important`,
      color: 'white !important',
    },
  },
})

const styles = {
  adlName: {
    marginBottom: 0,
    minHeight: 34,
  },
  titleInput: {
    paddingBottom: 3,
    fontSize: '2em',
    fontWeight: 400,
  },
  adlNameContainer: {
    flex: 1,
    paddingLeft: 15,
    fontWeight: 300,
  },
  divider: {
    height: 2,
    width: '100%',
  },
  errors: {
    marginBottom: 25,
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  iconContainer: {
    borderRadius: 4,
    textAlign: 'center',
    display: 'inline-block',
    verticalAlign: 'top',
    height: 40,
    width: 40,
  },
  icon: {
    color: 'white',
    height: 20,
    marginTop: 10,
  },
  quoteBox: {
    marginTop: 12,
  },
  titleContainer: {
    display: 'flex',
    paddingTop: 21,
    marginBottom: 12,
    marginTop: 12,
    background: colors.white,
    padding: '18px 22px',
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  buttonRow: {
    display: 'flex',
  },
  outComesTitle: { color: colors.black, opacity: 0.7 },
}

export default CarePlanSummaryHeader
